import { lazyInitializeCalendarsDataForEventDeeplink } from 'owa-calendar-bootstrap-utils';
import type { CalendarComposePopoutConfig, FullComposeViewState } from 'owa-calendar-compose-types';
import { FullComposeFormViewType } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { ClientItemId, getUserMailboxInfo } from 'owa-client-ids';
import type { MailboxInfo } from 'owa-client-types';
import { owaDate, timestamp, utcDate } from 'owa-datetime';
import { convertRestIdToEwsId } from 'owa-identifiers';
import { getDataFromParentWindow } from 'owa-popout-v2';
import { getUserConfiguration } from 'owa-session-store';
import { lazyShowFullComposeFromPriorState } from 'owa-calendar-compose-form-store';
import {
    getOptionsForFeature,
    OwsOptionsFeatureType,
    BohemiaOptions,
} from 'owa-outlook-service-options';
import registerFormOnShowOrchestrators from '../utils/registerFormOnShowOrchestrators';
import { initBootCalendarCloudSettings } from 'owa-calendar-cloud-settings/lib/bootIndex';
import { initializeDynamicLayout } from 'owa-layout';
import { lazyOpenCalendarFullCompose } from 'owa-calendar-compose-form-lifecycle';
import { lazyInitializeFullComposeFromQueryString } from 'owa-calendar-forms-deeplink-utils';
import { getQueryStringParameters } from 'owa-querystring';

export interface CalendarComposeRouteParameters {
    itemId?: string;
    account?: string;
}

export async function deepLinkComposeRouteHandler(parameters: CalendarComposeRouteParameters) {
    // read query parameters immediately before any URL rewrites potentially happen
    const queryParams = getQueryStringParameters();

    registerFormOnShowOrchestrators();

    initBootCalendarCloudSettings();

    // Initialize calendars
    await lazyInitializeCalendarsDataForEventDeeplink.importAndExecute();

    // Initialize dynamic layout
    // displayAds not enabled since this is a calendar scenario
    initializeDynamicLayout(false /* areDisplayAdsEnabled */);

    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (46,9): Type 'null' is not assignable to type 'MailboxInfo'.
    // @ts-expect-error
    let mailboxInfo: MailboxInfo = null;
    if (parameters.account) {
        mailboxInfo = getUserMailboxInfo(parameters.account);
    }

    const popoutConfig: CalendarComposePopoutConfig | null = await getDataFromParentWindow(
        parseCalendarComposePopoutConfig
    );

    if (popoutConfig?.includeFluidOnPopout) {
        let options = getOptionsForFeature<BohemiaOptions>(OwsOptionsFeatureType.Bohemia);
        options.fluidEnabledForTenant = true;
    }

    // There are multiple combinations of URL parameters and config data
    // which can be used to open a deeplink compose form. Attempt to open
    // each in priority order
    if (tryInitializeWithItemIdParameter(parameters, mailboxInfo)) {
        return;
    } else if (tryInitializeWithComposeConfig(popoutConfig)) {
        return;
    } else if (tryInitializeForReplyByMeeting(popoutConfig, mailboxInfo)) {
        return;
    } else if (tryInitializeFromPriorState(popoutConfig)) {
        return;
    } else {
        // Fall back to checking the url for query params and initializing
        // for creating a new event
        lazyInitializeFullComposeFromQueryString.importAndExecute(
            queryParams,
            FullComposeFormViewType.DeeplinkPopOut
        );
    }
}

function parseCalendarComposePopoutConfig(json: string): CalendarComposePopoutConfig {
    try {
        const popoutConfig = JSON.parse(json) as CalendarComposePopoutConfig;
        if (popoutConfig?.priorStateConfig) {
            fixComposeViewState(popoutConfig.priorStateConfig.viewState);
            fixEvent(popoutConfig.priorStateConfig.event);
            fixEvent(popoutConfig.priorStateConfig.originalEvent);
        }
        return popoutConfig;
    } catch {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (94,9): Type 'null' is not assignable to type 'CalendarComposePopoutConfig'.
        // @ts-expect-error
        return null;
    }
}

function fixEvent(event: CalendarEvent | null) {
    if (event) {
        const defaultTz = getUserConfiguration()?.UserOptions?.TimeZone;
        // OwaDates get serialized into the UTC isoString. (see owaDate::toJSON)
        // So we interpret them as UTC dates and then convert to the appropriate TZ
        event.Start = owaDate(event.StartTimeZoneId || defaultTz, utcDate(event.Start));
        event.End = owaDate(event.EndTimeZoneId || defaultTz, utcDate(event.End));
    }
}

function fixComposeViewState(viewState: FullComposeViewState | null) {
    if (viewState) {
        // Clear freeBusyData since it will be initialized again
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (114,9): Type 'null' is not assignable to type 'ObservableMap<string, ObservableMap<string, FreeBusyRow>>'.
        // @ts-expect-error
        viewState.freeBusyData = null;

        // Set view type to PopOut
        viewState.viewType = FullComposeFormViewType.DeeplinkPopOut;
    }
}

function tryInitializeWithItemIdParameter(
    parameters: CalendarComposeRouteParameters,
    mailboxInfo: MailboxInfo | null
) {
    if (!parameters.itemId) {
        return false;
    }

    // In case item Id is rest id, convert to EwsId.
    // If itemId is EWSId, then convertRestIdToEwsId returns the same id without any changes
    const id = convertRestIdToEwsId(parameters.itemId);

    const itemId: ClientItemId = {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (137,9): Type 'string | null' is not assignable to type 'string'.
        // @ts-expect-error
        Id: id,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (141,9): Type 'MailboxInfo | null' is not assignable to type 'MailboxInfo'.
        // @ts-expect-error
        mailboxInfo: mailboxInfo,
    };

    lazyOpenCalendarFullCompose.importAndExecute({
        fullComposeEntrySource: 'PopOut',
        viewType: FullComposeFormViewType.DeeplinkPopOut,
        itemId: itemId,
    });

    return true;
}

function tryInitializeWithComposeConfig(popoutConfig: CalendarComposePopoutConfig | null) {
    if (!popoutConfig?.composeConfig) {
        return false;
    }

    const { composeConfig } = popoutConfig;
    composeConfig.viewType = FullComposeFormViewType.DeeplinkPopOut;

    lazyOpenCalendarFullCompose.importAndExecute(composeConfig);

    return true;
}

function tryInitializeForReplyByMeeting(
    popoutConfig: CalendarComposePopoutConfig | null,
    mailboxInfo: MailboxInfo | null
) {
    if (!popoutConfig?.replyByMeetingConfig) {
        return false;
    }

    const {
        draftItemId,
        composeEntrySource,
        extractionSourceId,
        internetMessageId,
        shouldInitializeForNewDraft,
    } = popoutConfig.replyByMeetingConfig;

    const itemId: ClientItemId = {
        Id: draftItemId,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (187,9): Type 'MailboxInfo | null' is not assignable to type 'MailboxInfo'.
        // @ts-expect-error
        mailboxInfo: mailboxInfo,
    };

    const currentTimeInMs = timestamp();

    // post message back to parent window for removing
    // draft item on user calendar (minimize user confusion)
    window.addEventListener('beforeunload', event => {
        window.opener.postMessage(
            JSON.stringify({
                source: 'calDraftItem',
                id: itemId.Id,
                entry: composeEntrySource,
                EID: extractionSourceId,
                start: currentTimeInMs,
            }),
            window.location.origin
        );
    });

    lazyOpenCalendarFullCompose.importAndExecute({
        fullComposeEntrySource: 'PopOut',
        viewType: FullComposeFormViewType.DeeplinkPopOut,
        itemId: itemId,
        extractionSourceId: extractionSourceId,
        internetMessageId: internetMessageId,
        shouldInitializeForNewDraft: shouldInitializeForNewDraft,
    });

    return true;
}

function tryInitializeFromPriorState(popoutConfig: CalendarComposePopoutConfig | null) {
    if (!popoutConfig?.priorStateConfig) {
        return false;
    }

    lazyShowFullComposeFromPriorState.importAndExecute(popoutConfig.priorStateConfig);

    return true;
}
